import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, mergeMap, Observable, throwError } from 'rxjs';
import { environment, featureFlags } from '@app/environments/environment';
import { AuthService } from "@services/auth/auth.service";
import { v4 as uuidv4 } from 'uuid';

interface FileItem {
    app: string;
    value: string;
}

@Injectable({
  providedIn: 'root'
})
export class AskOurDocsV2Service {
  bearerToken : string = '';
  idToken : string = '';
  jsonIdTokenHeader : { [key: string]: any } = {};
  jsonBearerTokenHeader : { [key: string]: any } = {};

  private apiUrl = environment.askOurDocsIframeUrlV2;

  constructor(private http: HttpClient, protected authService: AuthService) {
    this.getLatestAccessToken();
    this.getLatestIdToken();
  }

  getLatestAccessToken() {
    this.authService.getAccessToken().pipe(
      map((idToken) => {
        this.bearerToken = idToken;
        this.jsonBearerTokenHeader = {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${this.bearerToken}`
        }
        return true;
      }))
    .subscribe();
  }

  getLatestIdToken() {
    this.authService.getIdToken().pipe(
      map((idToken) => {
        this.idToken = idToken;
        this.jsonIdTokenHeader = {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${this.idToken}`
        }
        return true;
      }))
    .subscribe();
  }

  private getApiUrl(endpoint: string, param?: string): string {
    const newEndpointMapping: { [key: string]: string } = {
      'get_details': 'repo/config_details',
      'save_to_db': 'conversation/save',
      'conversation': 'conversation/chat',
      'history/list': 'conversation/list',
      'history/get': 'conversation/get',
      'history/rename': 'conversation/rename',
      'history/delete': 'conversation/delete',
      'save-admin-feedback-form': 'feedback/save',
      'get-feedback-form': 'feedback/fetch',
      'get-default-feedback-form': 'feedback/default',
      'feedback': 'feedback/response',
      'save_prompt': 'prompt/save',
      'fetch_prompts': 'prompt/fetch',
      'delete_prompt': 'prompt/delete',
      'update_prompt': 'prompt/update',
      'get-public-prompts': 'prompt/public/fetch',
      'get_last_run_date': 'repo/last-indexed-date',
      'get_sas_token': 'storage/sas/get',
      'list-files': 'storage/file/list',
      'get-file-content': 'storage/file/fetch',
      'save-file-content': 'storage/file/save',
    };

    // Use new endpoints only when the feature flag is true
    const mappedEndpoint = featureFlags.aodApiVersionUpgrade
      ? newEndpointMapping[endpoint] || endpoint
      : endpoint;

    let baseUrl = featureFlags.aodApiVersionUpgrade
      ? `${this.apiUrl}/api/v2`
      : `${this.apiUrl}`;

    // Handle old behavior for conversation and history endpoints
    if (!featureFlags.aodApiVersionUpgrade && (endpoint.startsWith('conversation') || endpoint.startsWith('history'))) {
      baseUrl = `${this.apiUrl}`;
    } else if (!featureFlags.aodApiVersionUpgrade) {
      baseUrl = `${this.apiUrl}/api`;
    }

    // Append the parameter to the endpoint if provided
    const finalEndpoint = param ? `${mappedEndpoint}/${param}` : mappedEndpoint;

    return `${baseUrl}/${finalEndpoint}`;
  }

  // Get Details API
  async getDetails (indexName: string): Promise<any> {
    return this.http.post(this.getApiUrl('get_details'),
      {index_name : indexName},
      {
        headers: this.jsonBearerTokenHeader
      }).toPromise();
  }

  async conversationApi(conversationApiPayload: any): Promise<Response> {
    await this.getLatestAccessToken();
    let abortController = new AbortController();
    return fetch(this.getApiUrl('conversation'), {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify(conversationApiPayload),
      signal: abortController.signal
    });
  }

  async saveToDB(data: any): Promise<Response> {
    await this.getLatestAccessToken();

    const payload = featureFlags.aodApiVersionUpgrade
        ? { ...data, indexerName: data.indexName, indexName: undefined } // Rename indexName to indexerName
        : { ...data };

    const response = await fetch(this.getApiUrl('save_to_db'), {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({
        data: payload
      }),
    });
    return response;
  }

  async getSasToken(indexerName: string): Promise<{sas_token: string, base_url: string}> {
    await this.getLatestAccessToken();
    const paramName = featureFlags.aodApiVersionUpgrade ? 'indexer_name' : 'index_name';
    const response = await fetch(this.getApiUrl('get_sas_token'), {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({ [paramName]: indexerName })
    });
    const data = await response.json();
    return { sas_token: data.sas_token, base_url: data.base_url };
  }

  getChatHistory(indexerName: string, offset? : number, limit? : number, order? : string): Promise<any> {
    const paramName = featureFlags.aodApiVersionUpgrade ? 'indexerName' : 'indexName';
    const params = new HttpParams()
      .set(paramName, indexerName)
      .set('offset', offset?.toString() || '0')
      .set('limit', limit?.toString() || '10000')
      .set('order', order?.toString() || 'DESC');
    return this.http.get(this.getApiUrl('history/list'), {
      params,
      headers: this.jsonBearerTokenHeader
    }).toPromise();
  }

  renameConversation(conversationId: string, newTitle: string): Promise<any> {
    const payload = {
      conversation_id: conversationId,
      new_title: newTitle
    };
    return this.http.post(this.getApiUrl('history/rename'), payload, {
      headers: this.jsonBearerTokenHeader
    }).toPromise();
  }

  deleteConversation(conversationId: string): Promise<any> {
    return this.http.delete(this.getApiUrl('history/delete'), {
      params: {
        conversation_id: conversationId
      },
      headers: {
        'Authorization': `Bearer ${this.bearerToken}`
      }
    }).toPromise();
  }

  openChat(conversationId : string){
    return this.http.get(this.getApiUrl('history/get'), {
      params:{
        conversationId: conversationId
      },
      headers: {
        'Authorization': `Bearer ${this.bearerToken}`
      }
    }).toPromise();
  }

  async getLastRunDate(indexer_name:string): Promise<{last_run_date: string}> {
    await this.getLatestAccessToken();
    const response = await fetch(this.getApiUrl('get_last_run_date'), {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({ indexer_name: indexer_name })
    });
    const data = await response.json();
    const last_run_date = data.last_run_date ?? data.repo_last_indexed_date;
    return { last_run_date };
  }

  getFeedbackFormData(indexName: string): Observable<any> {
    const url = this.getApiUrl('get-feedback-form', indexName);
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);
    return this.http.get(url, { headers }).pipe(
      map(response => {
        return response;
      })
    );
  }

  async saveFeedback(data: any, messageId: string): Promise<any> {
    const url = this.getApiUrl('feedback', messageId);
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);

    return this.http.post(url, { ...data }, { headers }).toPromise();
  }

  async saveCustomizableFeedbackForm(data: any): Promise<any> {
    const url = this.getApiUrl('save-admin-feedback-form');
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);

    return this.http.post(url,data, { headers }).toPromise();
  }

  getDefaultFeedbackForm(): Observable<any> {
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);
    return this.http.get(this.getApiUrl('get-default-feedback-form'), { headers }).pipe(
      map(response => {
        return response;
      })
    );
  }

  // ConfigurationService methods
  getFiles(): Observable<FileItem[]> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.idToken}`
    });
    return this.http.get<FileItem[]>(this.getApiUrl('list-files'), { headers });
  }

  getFileContent(fileName: string): Observable<{ [key: string]: any }> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.bearerToken}`
    });
    return this.http.get<{ [key: string]: any }>(this.getApiUrl('get-file-content',fileName), { headers });
  }

  saveFileContent(fileName: string, data: any): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.bearerToken}`
    });
    const url = this.getApiUrl('save-file-content',fileName);
    return this.http.post(url, data, { headers });
  }

  async savePrompt(id: string, indexName: string, category: string, title: string, description: string): Promise<{ message: string }> {
    try {
      await this.getLatestAccessToken();

      const response = await fetch(this.getApiUrl('save_prompt'), {
        method: "POST",
        headers: this.jsonBearerTokenHeader,
        body: JSON.stringify({
          promptId: id,
          indexName: indexName,
          category: category,
          promptTitle: title,
          promptDescription: description
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to save prompt: ${response.statusText}`);
      }

      const data = await response.json();
      return { message: data.message };

    } catch (error: any) {
      console.error('Error in savePrompt:', error);
      throw new Error(error.message || 'An unexpected error occurred while saving the prompt.');
    }
  }

  async getSavedPromptsPrivate(indexName: string): Promise<{savedPrompts: any}> {
    await this.getLatestAccessToken();
    const response = await fetch(this.getApiUrl('fetch_prompts'), {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({ indexName: indexName })
  });
  const data = await response.json();
        return { savedPrompts: data.prompts };
  }

  async getSavedPromptsPublic(indexName: string): Promise<{savedPrompts: any}> {
    await this.getLatestAccessToken();
    const response = await fetch(this.getApiUrl('get-public-prompts'), {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({ indexName: indexName })
  });
  const data = await response.json();
        return { savedPrompts: data.tile_prompts };
  }

  async updatePrivatePrompt(indexName: string, prompt: any): Promise<{message: any}> {
    await this.getLatestAccessToken();
    const response = await fetch(this.getApiUrl('update_prompt'), {
      method: "PUT",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({
        indexName: indexName,
        promptId: prompt.id,
        promptTitle: prompt.promptTitle,
        promptDescription: prompt.promptDescription,
      })
  });
  const data = await response.json();
        return { message: data.message };
  }

  async deletePrivatePrompt(indexName: string, prompt: any): Promise<{message: any}> {
    await this.getLatestAccessToken();
    const response = await fetch(this.getApiUrl('delete_prompt'), {
      method: "DELETE",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({
        indexName: indexName,
        promptId: prompt.id
      })
  });
  const data = await response.json();
        return { message: data.message };
  }

  async getFileWithPageNumber(url: string, chunk: string): Promise<{ pageNumber: number }> {
    const apiUrl = `${this.apiUrl}/api/v2/storage/file/find-page-number`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: this.jsonBearerTokenHeader,
      body: JSON.stringify({ url, chunk })
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file with page number: ${response.statusText}`);
    }

    const data = await response.json();

    return { pageNumber: data.pageNumber };
  }

  getFeedbackReport(payload : any) {
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);
    const apiUrl = `${this.apiUrl}/api/v2/feedback/filter`;
    return this.http.post<any[]>(apiUrl, payload, {headers});
  }

  // Repository public status methods
  getRepositoryPublicStatus(indexName: string): Observable<any> {
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);
    const apiUrl = `${this.apiUrl}/api/v2/repo/fetch`;
    const params = new HttpParams().set('index_name', indexName);
    return this.http.get(apiUrl, { headers, params });
  }

  // Save or update repository public status
  // Uses /repo/save for first-time public repositories, /repo/update for existing ones
  updateRepositoryPublicStatus(repositoryId: string, indexName: string, isPublic: boolean): Observable<any> {
    const headers = new HttpHeaders(this.jsonBearerTokenHeader);
    
    // If repositoryId is empty, it means this is the first time making the repository public
    // Use the save endpoint to create a new repository record
    if (!repositoryId && isPublic) {
      const apiUrl = `${this.apiUrl}/api/v2/repo/save`;
      return this.http.post(apiUrl, { 
        id: uuidv4(), // Use indexName as id for new repositories
        indexName: indexName, 
        isPublic: isPublic 
      }, { headers });
    }
    
    // Use the update endpoint for existing repositories
    const apiUrl = `${this.apiUrl}/api/v2/repo/update`;
    return this.http.put(apiUrl, { 
      id: repositoryId,
      indexName: indexName, 
      isPublic: isPublic 
    }, { headers });
  }

  fetchPublicRepositories(): Observable<any[]> {
    // Ensure we have a fresh access token
    return this.authService.getAccessToken().pipe(
      map((token) => {
        this.bearerToken = token;
        this.jsonBearerTokenHeader = {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${this.bearerToken}`
        };
        return token;
      }),
      mergeMap(() => {
        const headers = new HttpHeaders(this.jsonBearerTokenHeader);
        const apiUrl = `${this.apiUrl}/api/v2/repo/fetch`;
        const params = new HttpParams().set('is_public', 'true');
        return this.http.get<{repositories: any[]}>(apiUrl, { headers, params }).pipe(
          map(response => {
            return response.repositories || [];
          }),
          catchError(error => {
            console.error('Error fetching public repositories:', error);
            // Return empty array on error to prevent breaking the UI
            return throwError(() => error);
          })
        );
      })
    );
  }
}

export { FileItem };
